/**
 * The formatter vocabulary the editor offers for a placeholder, and how a placeholder's
 * formatters are shown on its chip. Names are the canonical spellings FormatterPipeline.cs
 * (lib/src/Services) matches; the pipeline accepts more aliases than are listed here, and a
 * formatter the editor does not list is still kept and shown as-is.
 */
import type { FormatterSpec } from './types'

export interface FormatterArg {
  label: string
  placeholder?: string
  /** A closed set of values, offered as a select instead of free text. */
  choices?: string[]
  defaultValue?: string
}

export interface FormatterDef {
  name: string
  group: 'Text' | 'Date' | 'Number' | 'Array' | 'Image & code'
  arg?: FormatterArg
}

export const FORMATTERS: FormatterDef[] = [
  { name: 'upper', group: 'Text' },
  { name: 'lower', group: 'Text' },
  { name: 'title', group: 'Text' },
  { name: 'trim', group: 'Text' },
  { name: 'truncate', group: 'Text', arg: { label: 'Length', placeholder: '100', defaultValue: '100' } },
  { name: 'default', group: 'Text', arg: { label: 'Fallback text', placeholder: 'N/A' } },
  { name: 'date', group: 'Date', arg: { label: 'Format', placeholder: 'MMMM d, yyyy', defaultValue: 'MMMM d, yyyy' } },
  { name: 'dateshort', group: 'Date' },
  { name: 'datelong', group: 'Date' },
  { name: 'datetime', group: 'Date' },
  { name: 'year', group: 'Date' },
  { name: 'month', group: 'Date' },
  { name: 'day', group: 'Date' },
  { name: 'number', group: 'Number', arg: { label: 'Pattern', placeholder: 'N2', defaultValue: 'N2' } },
  { name: 'round', group: 'Number', arg: { label: 'Decimals', placeholder: '0', defaultValue: '0' } },
  { name: 'floor', group: 'Number' },
  { name: 'ceil', group: 'Number' },
  { name: 'currency', group: 'Number', arg: { label: 'Currency', choices: ['USD', 'GBP', 'EUR', 'JPY'], defaultValue: 'USD' } },
  { name: 'count', group: 'Array' },
  { name: 'sum', group: 'Array' },
  { name: 'join', group: 'Array', arg: { label: 'Separator', placeholder: ', ', defaultValue: ', ' } },
  { name: 'image', group: 'Image & code' },
  { name: 'qr', group: 'Image & code' },
  { name: 'code128', group: 'Image & code' },
  { name: 'ean13', group: 'Image & code' },
  { name: 'datamatrix', group: 'Image & code' },
  { name: 'bar', group: 'Image & code' },
  { name: 'line', group: 'Image & code' },
  { name: 'pie', group: 'Image & code' },
]

export const FORMATTER_GROUPS: FormatterDef['group'][] = ['Text', 'Date', 'Number', 'Array', 'Image & code']

export function formatterDef(name: string): FormatterDef | undefined {
  const n = name.toLowerCase()
  return FORMATTERS.find(f => f.name === n)
}

/** Port of FormatterSyntax.QuoteArg: quotes an argument the template syntax would otherwise split. */
export function quoteFormatterArg(arg: string): string {
  if (arg.length === 0) return arg
  const needsQuotes = /[,|:]/.test(arg) || /^\s|\s$/.test(arg)
  if (!needsQuotes) return arg
  return arg.includes("'") ? `"${arg}"` : `'${arg}'`
}

export function formatterText(spec: FormatterSpec): string {
  return spec.args.length ? `${spec.name}:${spec.args.map(quoteFormatterArg).join(',')}` : spec.name
}

/** The chip text: `{{rawName}}`, or `{{rawName | f1:arg | f2}}` in the syntax a template uses. */
export function placeholderLabel(rawName: string, formatters: FormatterSpec[] | null | undefined): string {
  const tail = (formatters ?? []).map(f => ` | ${formatterText(f)}`).join('')
  return `{{${rawName}${tail}}}`
}
